// Rendering Cherry's REAL financing terms — verbatim, never recomputed.
//
// KEEP IN SYNC with tx-presenter-service/lib/pdf/format.js (cherryAmountFor / cherryPlanKey /
// normalizeHiddenPlanKeys / cherryPlansFor / cherryTierRows / cherryHeadline / cherryDisclosure
// twins; cherryEditableRows is Builder-only and has none) — the signed PDF renders the same
// rows, and a signed plan that disagrees with the screen is a financial agreement that
// disagrees with what the patient was shown. The canonical fixture is duplicated VERBATIM in
// tests/component/paymentOptionAmountLines.spec.js and cypress/node-tests/cherry-terms-render.test.js.
//
// ⛔ KEEP THIS FILE IMPORT-FREE. cypress/node-tests/cherry-terms-render.test.js executes this
// exact file by stripping the ESM keywords; an import would make that load fail (and
// cherry-no-typed-tier-leak.test.js guards it). paymentOptions.js may import from here — never
// the reverse.
//
// ── Why this exists at all ───────────────────────────────────────────────────────────────
// A `finance` option's deferredMonths/aprMonths/apr are the practice's own typed estimate, and
// paymentOptions.js amortizes a monthly payment from them. Measured against both pilot
// practices' live Cherry pricing on 2026-08-06, those numbers understated the lender's actual
// cost — 14.9% advertised against 17.8/18.8/19.8/20.8% charged, up to $1,760 of undisclosed
// interest on a $10,000 60-month plan — while simultaneously under-selling the practice's own
// 0% band (they showed 6/12 months; Cherry gives them 0% through 18).
//
// So when an option is Cherry-backed we do not compute anything. Cherry has already done the
// math; we display it. That also sidesteps authoring credit-terms arithmetic, which is not a
// thing a practice-management tool should be doing.

/**
 * ⛔ Cherry's `term` EXCLUDES the down payment. TOTAL PAYMENTS = term + 1.
 *
 * Verified against 12 of Harrisburg's real zero-interest plans (amount/(term+1) reproduces
 * every one; amount/term reproduces none). The backend computes `totalPayments` once, in
 * cherry-service/lib/cherry-client.js normalizePlan, so nothing here has to remember — but the
 * LABEL still has to say it out loud, because "12 months" reads as twelve payments to everyone
 * who has ever seen a payment plan, and it is thirteen.
 */
export function isCherryBacked(opt) {
  return !!(opt && opt.type === 'finance' && opt.provider === 'cherry');
}

/**
 * Are these stored terms still valid for the amount currently on screen?
 *
 * Cherry's plan SET depends on the amount — Harrisburg has no 60-month plan at $1,000, and the
 * biweekly option disappears above $2,500 — so a menu priced for a different portion is not
 * "close enough", it is a different offer. The TC changing a procedure must invalidate it.
 *
 * One cent of tolerance, purely for float noise in the portion sum. Deliberately NOT a
 * percentage band: "near enough" on a credit quote is the kind of leniency that ends up
 * showing a patient terms for a number they never saw.
 */
export function cherryTermsMatch(terms, portion) {
  if (!terms || typeof terms !== 'object') return false;
  const priced = parseFloat(terms.purchaseAmount);
  const now = parseFloat(portion);
  if (!Number.isFinite(priced) || !Number.isFinite(now)) return false;
  // Whole cents on both sides. `Math.abs(priced - now) < 0.01` lets a real one-cent difference
  // through (2500.01 - 2500 is 0.00999… in floating point).
  return Math.round(priced * 100) === Math.round(now * 100);
}

/**
 * The amount Cherry is asked to price — AND the amount stored terms are matched against.
 *
 * It is the breakdown's `remainingAfterToday`: the patient portion, plus the option's own fee,
 * minus any chairside Pay today. One definition, used by the Builder (what to fetch), Present
 * Mode and the patient view (what to match), because pricing one number and matching another
 * is how a patient gets shown terms for an amount they never saw.
 *
 * Rounded to the cent so float noise in the portion sum can't make a priced menu look stale.
 * `null` when the breakdown carries no finite figure.
 */
export function cherryAmountFor(breakdown) {
  const n = breakdown ? parseFloat(breakdown.remainingAfterToday) : NaN;
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
}

/**
 * A plan's identity: term + cadence, `"12-MONTHLY"`, `"3-BIWEEKLY"`. NOT term alone — term 3
 * legitimately exists twice for one amount (MONTHLY + BIWEEKLY), and hiding one must not hide
 * the other. This is THE key builder: the display rows (cherryTierRows), the hide lists
 * (`cherryHiddenPlans` / `cherryHiddenPlansPatient`) and the backend plan catalog
 * (cherry-service `key`) all use exactly this shape. A missing cadence reads as MONTHLY.
 */
export function cherryPlanKey(p) {
  const term = parseInt(p && p.term, 10) || 0;
  return `${term}-${String((p && p.periodLength) || 'MONTHLY').toUpperCase()}`;
}

/**
 * A stored hide list as clean keys: strings only, trimmed, upper-cased (the same case
 * cherryPlanKey produces), de-duplicated, order kept. Anything that is not an array — a
 * hand-edited blob, a string, null — is an EMPTY list, i.e. nothing hidden: junk must never
 * silently hide the whole menu. Twin of the PDF layer's copy.
 */
export function normalizeHiddenPlanKeys(list) {
  if (!Array.isArray(list)) return [];
  const out = [];
  list.forEach((k) => {
    if (typeof k !== 'string') return;
    const key = k.trim().toUpperCase();
    if (key && out.indexOf(key) === -1) out.push(key);
  });
  return out;
}

/**
 * The plans this option is allowed to show. Shown = Cherry's plans
 *   → the legacy `opt.cherryTermMonths` allow-filter, if non-empty
 *   → minus the practice's hide list  `opt.cherryHiddenPlans`        (Settings)
 *   → minus the patient's hide list   `opt.cherryHiddenPlansPatient` (Builder, this patient only)
 * (hide lists hold cherryPlanKey strings; see D2 in docs/plans/2026-10-16-cherry-plan-catalog-plan.md).
 *
 * The legacy filter matches `plan.term` in ANY cadence, so a 3 keeps both the monthly and the
 * biweekly 3. Settings no longer writes it (no site had one), but a stored one is still honoured
 * as cheap insurance. An absent, empty or all-invalid filter means ALL plans — "show everything
 * Cherry offers" is the default, and a hand-edited blob must never silently hide the whole menu.
 * The same default holds for both hide lists: absent / empty / junk = nothing hidden.
 *
 * All plans hidden is not special-cased here: the result is [], which hasUsableCherryTerms reads
 * as "no usable terms" → the no-figures line, never the practice's typed tiers.
 */
export function cherryPlansFor(terms, opt) {
  let plans = (terms && Array.isArray(terms.plans) ? terms.plans : []).filter((p) => p && typeof p === 'object');
  // Numbers or numeric strings (a settings input can hand back "12") — same rule as the PDF twin.
  const wanted = (opt && Array.isArray(opt.cherryTermMonths) ? opt.cherryTermMonths : [])
    .filter((v) => typeof v === 'number' || typeof v === 'string')
    .map(Number)
    .filter((n) => Number.isInteger(n) && n > 0);
  if (wanted.length) plans = plans.filter((p) => wanted.indexOf(Number(p.term)) !== -1);
  const hidden = normalizeHiddenPlanKeys(opt && opt.cherryHiddenPlans)
    .concat(normalizeHiddenPlanKeys(opt && opt.cherryHiddenPlansPatient));
  if (hidden.length) plans = plans.filter((p) => hidden.indexOf(cherryPlanKey(p)) === -1);
  return plans;
}

/**
 * Should this option show Cherry figures right now?
 *
 * FALSE means show NO NUMBERS — never the typed tiers. Falling back to them would reinstate
 * exactly the understated terms this feature exists to remove, and it would do it while
 * looking authoritative, which is worse than the original problem.
 *
 * A term filter that leaves nothing is "no terms", not "an empty menu": the patient is told
 * we'll confirm, rather than shown a financing card with no figures on it.
 */
export function hasUsableCherryTerms(opt, terms, amount) {
  return isCherryBacked(opt)
    && cherryTermsMatch(terms, amount)
    && cherryPlansFor(terms, opt).length > 0;
}

function money(n) {
  const num = parseFloat(n) || 0;
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const CADENCE = { MONTHLY: 'monthly', BIWEEKLY: 'biweekly', WEEKLY: 'weekly' };

function cadenceWord(periodLength) {
  return CADENCE[String(periodLength || '').toUpperCase()] || 'monthly';
}

/**
 * One display row per Cherry plan: { key, label, amount, note }.
 *
 *   label  "12 monthly payments · 0% APR"
 *   amount "$192.31"
 *   note   "after $192.31 today · 13 payments total · no interest"
 *
 * `key` is `term + periodLength`, NOT term alone — term 3 legitimately appears twice for the
 * same amount, once MONTHLY and once BIWEEKLY. A v-for keyed on term silently drops one.
 *
 * Every figure is Cherry's own. Nothing here multiplies, amortizes or rounds a payment.
 */
export function cherryTierRows(terms, opt) {
  return cherryPlansFor(terms, opt).map((p) => {
    const term = parseInt(p.term, 10) || 0;
    const total = parseInt(p.totalPayments, 10) || (term + 1);
    const apr = parseFloat(p.apr) || 0;
    const charge = parseFloat(p.financeCharge) || 0;
    const cadence = cadenceWord(p.periodLength);

    const aprText = apr > 0 ? `${apr}% APR` : '0% APR';
    const interest = charge > 0 ? `${money(charge)} interest` : 'no interest';

    return {
      key: cherryPlanKey(p),
      label: `${term} ${cadence} payment${term === 1 ? '' : 's'} · ${aprText}`,
      amount: money(p.paymentAmount),
      // The down payment is stated explicitly. It is the difference between what Cherry's
      // "12 month" plan actually is and what the phrase implies, and a patient signing a plan
      // is entitled to know a payment is due at checkout.
      note: `after ${money(p.paymentAmount)} today · ${total} payments total · ${interest}`,
      apr,
      term,
      periodLength: p.periodLength || null,
    };
  });
}

/**
 * Builder only (no PDF twin - nothing hidden is ever rendered anywhere else): one row per plan
 * Cherry priced, INCLUDING the hidden ones, so the TC can see and toggle them.
 *
 *   { ...cherryTierRows row, practiceHidden, patientHidden, shown }
 *
 * `shown` is exactly what cherryPlansFor keeps. A plan hidden in Settings stays practiceHidden
 * and never reads as patientHidden: the Builder can hide MORE for one patient but cannot un-hide
 * what the practice hid (D3), so that row is displayed greyed and is not togglable.
 */
export function cherryEditableRows(terms, opt) {
  const practice = normalizeHiddenPlanKeys(opt && opt.cherryHiddenPlans);
  const patient = normalizeHiddenPlanKeys(opt && opt.cherryHiddenPlansPatient);
  // Rows for every plan the legacy filter allows, ignoring both hide lists.
  const all = cherryTierRows(terms, { ...opt, cherryHiddenPlans: [], cherryHiddenPlansPatient: [] });
  return all.map((r) => {
    const practiceHidden = practice.indexOf(r.key) !== -1;
    const patientHidden = !practiceHidden && patient.indexOf(r.key) !== -1;
    return { ...r, practiceHidden, patientHidden, shown: !practiceHidden && !patientHidden };
  });
}

/**
 * The one line a payment-option CARD shows: the lowest payment Cherry offers.
 *
 *   { headline: 'As low as $63.70/mo', sub: '60 monthly payments · 20.8% APR' }
 *
 * Monthly plans are preferred (the card says "/mo"); if Cherry offers none, the lowest plan
 * overall is quoted in its own cadence ("As low as $625.00 biweekly"). Ties keep the first plan.
 *
 * Unlike the typed-tier headline there is no "~" and no rounding: the figure is Cherry's, to the
 * cent, and it is formatted HERE — never by a caller's formatter, which may round or drop cents.
 * `null` when there are no plans to quote.
 */
export function cherryHeadline(terms, opt) {
  // A plan with no positive payment is not something to quote ("As low as $0.00").
  const plans = cherryPlansFor(terms, opt).filter((p) => parseFloat(p.paymentAmount) > 0);
  if (!plans.length) return null;

  const isMonthly = (p) => !p.periodLength || String(p.periodLength).toUpperCase() === 'MONTHLY';
  const monthly = plans.filter(isMonthly);
  const pool = monthly.length ? monthly : plans;
  const amountOf = (p) => parseFloat(p.paymentAmount) || 0;
  let best = pool[0];
  pool.forEach((p) => { if (amountOf(p) < amountOf(best)) best = p; });

  const term = parseInt(best.term, 10) || 0;
  const apr = parseFloat(best.apr) || 0;
  const cadence = cadenceWord(best.periodLength);
  return {
    headline: isMonthly(best)
      ? `As low as ${money(best.paymentAmount)}/mo`
      : `As low as ${money(best.paymentAmount)} ${cadence}`,
    sub: `${term} ${cadence} payment${term === 1 ? '' : 's'} · ${apr > 0 ? apr : 0}% APR`,
  };
}

/**
 * The line that must accompany every Cherry figure: they are estimates, as of a moment, and
 * Cherry — not the practice — sets the final terms. `dateText` is pre-formatted by the caller
 * (site timezone); falsy drops the "as of" clause rather than printing "as of undefined".
 */
export function cherryDisclosure(dateText) {
  return dateText
    ? `Estimates from Cherry as of ${dateText}. Final terms are set by Cherry when you apply.`
    : 'Estimates from Cherry. Final terms are set by Cherry when you apply.';
}

/**
 * The fetched-at instant as the disclosure's date text, in the office's timezone:
 * "October 10, 2026 at 11:00 AM". '' when absent or invalid. Same formatter and options as the
 * PDF twin (format.js cherryAsOfText → formatSignedAt) so screen and signed PDF read alike.
 */
export function cherryAsOfText(terms, timezone) {
  const iso = terms && terms.fetchedAt;
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const opts = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' };
  if (timezone) {
    try {
      return d.toLocaleString('en-US', { timeZone: timezone, ...opts });
    } catch (e) { /* invalid tz → fall through to no-tz */ }
  }
  return d.toLocaleString('en-US', opts);
}

/** What to say when there are no usable terms. Never a number. */
export const CHERRY_NO_TERMS_TEXT = "Financing available — we'll confirm your terms.";

export default {
  isCherryBacked,
  cherryAmountFor,
  cherryTermsMatch,
  cherryPlanKey,
  normalizeHiddenPlanKeys,
  cherryPlansFor,
  hasUsableCherryTerms,
  cherryTierRows,
  cherryEditableRows,
  cherryHeadline,
  cherryDisclosure,
  cherryAsOfText,
  CHERRY_NO_TERMS_TEXT,
};
